import { z } from "zod";

/**
 * Zod's default messages, rewritten for the person reading them.
 *
 * Every validation message in this product reaches a human. An operator saving
 * a cookie-consent banner was shown "Too small: expected string to have >=1
 * characters" (b88cbdb0, b7865d78); an itinerary title over the limit produced
 * "Too big: expected string to have <=200 characters", which one reporter
 * described as "a raw validation error from a library (such as Zod)" —
 * correctly (412032c1). Zod's defaults are written for whoever wrote the
 * schema, and that is the only audience they suit.
 *
 * Installed globally rather than annotated schema by schema. There are
 * hundreds of constraints across the DTOs and the ones that had been given
 * copy were given it one report at a time, which is why the same sentence kept
 * resurfacing on a different screen. A global map covers every constraint that
 * exists now and every one added later, without anyone remembering.
 *
 * It does NOT override a message a schema sets for itself — Zod resolves the
 * schema-level error first — so the specific wording already written for FAQ
 * questions, checkout field labels and the rest still wins. This is the floor,
 * not the ceiling: reach for a per-field message whenever the field has
 * something more useful to say than "this field is required".
 *
 * Codes it does not recognise fall through to Zod, whose own text for those is
 * already readable ("Invalid email address", "Invalid UUID").
 */
export function humanErrorMap(issue: z.core.$ZodRawIssue): string | undefined {
  switch (issue.code) {
    case "invalid_type":
      // Zod reports a missing or null field as a type error, and that is by far
      // the common case — "expected string, received undefined" means the
      // person left it blank.
      //
      // A value that IS present but the wrong type used to fall through to
      // Zod, whose text for it ("Invalid input: expected string, received
      // number") is a sentence about JavaScript addressed to whoever wrote the
      // schema. Rare from a form, but reachable from a stale client or a
      // hand-made request, and there is nothing useful to tell someone about
      // the runtime type of what they sent.
      return issue.input === undefined || issue.input === null
        ? "This field is required"
        : "That value isn't valid here";

    case "too_small":
      return tooSmall(issue);

    case "too_big":
      return tooBig(issue);

    default:
      return undefined;
  }
}

function tooSmall(issue: z.core.$ZodRawIssue): string | undefined {
  const min = Number(
    (issue as { minimum?: number | bigint }).minimum ?? Number.NaN,
  );
  if (Number.isNaN(min)) return undefined;

  switch (issue.origin) {
    case "string":
      // A blank field, not a length problem, whenever one character would do.
      return min <= 1
        ? "This field is required"
        : `Must be at least ${min} characters`;
    case "date":
      return `Must be on or after ${formatDate(min)}`;
    case "array":
    case "set":
      return min <= 1 ? "Add at least one" : `Add at least ${min}`;
    case "number":
    case "int":
    case "bigint":
      // `.positive()` / `.gt()` are exclusive: "0 or more" would name the one
      // value the rule refuses.
      return issue.inclusive === false
        ? `Must be more than ${format(min)}`
        : `Must be ${format(min)} or more`;
    default:
      return undefined;
  }
}

function tooBig(issue: z.core.$ZodRawIssue): string | undefined {
  const max = Number(
    (issue as { maximum?: number | bigint }).maximum ?? Number.NaN,
  );
  if (Number.isNaN(max)) return undefined;

  switch (issue.origin) {
    case "string":
      return `Must be ${format(max)} characters or fewer`;
    case "date":
      return `Must be on or before ${formatDate(max)}`;
    case "array":
    case "set":
      return max === 1 ? "Only one allowed" : `No more than ${format(max)}`;
    case "number":
    case "int":
    case "bigint":
      return issue.inclusive === false
        ? `Must be less than ${format(max)}`
        : `Must be ${format(max)} or less`;
    default:
      return undefined;
  }
}

/** Thousands separators, because these bounds get large — the pricing cap
 *  reads as "10,000,000,000", not a wall of ten zeroes. */
function format(n: number): string {
  return n.toLocaleString("en-US");
}

/** Zod reports a date bound as epoch milliseconds, so the untouched message
 *  reads "expected date to be <=1767225600000" — a number no one can act on.
 *  Nothing else in these two functions needs a unit-aware formatter; dates do,
 *  because the raw form is not merely terse but unreadable. */
function formatDate(ms: number): string {
  return new Date(ms).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Install the map. Called once, at `@tripcart/shared` import time, so every
 * app gets it without each one remembering to opt in — the failure this
 * replaces was per-surface, so a per-app install would reproduce it.
 */
export function installHumanErrorMap(): void {
  z.config({ customError: humanErrorMap });
}

/**
 * The largest value a Postgres `integer` column holds.
 *
 * Every money column in this product is `integer` — `experience_prices.
 * price_minor`, `experience_packages.base_price_minor`, `addons.price_minor`
 * and the rest — as is `experience_settings.booking_cutoff_minutes`. A schema
 * bound looser than this does not permit a bigger number, it just moves where
 * the refusal happens: past validation, into the INSERT, and back out as
 * "A database error occurred while saving" with no field named (e91305e0,
 * 85f6b327, 894d446c, bddb04a7 — a flat price of 55,023,300.00 is 5.5 billion
 * minor units, which cleared a 10-billion schema bound and then overflowed a
 * 2.1-billion column).
 *
 * Bound integer-backed numbers by this so the limit is enforced where it can
 * be explained.
 */
export const PG_INT_MAX = 2_147_483_647;
